/**
 * Phase 0 Diagnostic Page (preview-only, READ-ONLY)
 *
 * Purpose: Verify from within the Emergent preview environment that
 *   https://entorid-platform.emergent.host  (declared Android production API)
 * is reachable, alive, and to enumerate its endpoint surface.
 *
 * SAFETY GUARANTEES:
 *  - Uses its OWN axios instance — REACT_APP_BACKEND_URL is NOT changed.
 *  - Sends only GET/OPTIONS. No POST/PUT/PATCH/DELETE.
 *  - No admin login attempts (no credentials shipped).
 *  - Does not touch DNS, nginx, production DB, or the Android app.
 *  - Renders auth UI shells (LoginView / SignupView from CustomerAuthModal)
 *    purely for visual confirmation — those forms never submit here.
 *
 * Route: /phase0 (preview only). Not linked from anywhere.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, AlertTriangle, ShieldCheck } from 'lucide-react';

const ANDROID_API = 'https://entorid-platform.emergent.host';

// Read-only axios — GET/OPTIONS only, generous timeout
const readAxios = axios.create({
  baseURL: ANDROID_API,
  timeout: 10000,
  validateStatus: () => true, // never throw — we report every status
});

// A small, curated set of endpoints to probe (all public / safe reads)
const PROBES = [
  { method: 'GET',     path: '/docs',                              label: 'Swagger UI' },
  { method: 'GET',     path: '/openapi.json',                      label: 'OpenAPI schema' },
  { method: 'GET',     path: '/api/services',                      label: 'Public services list' },
  { method: 'OPTIONS', path: '/api/auth/login',                    label: 'Auth login (OPTIONS only)' },
  { method: 'OPTIONS', path: '/api/auth/otp/send',                 label: 'OTP send (OPTIONS only)' },
  { method: 'OPTIONS', path: '/api/auth/register',                 label: 'Auth register (OPTIONS only)' },
  { method: 'GET',     path: '/api/panel/gateway/login',           label: '(This codebase route) Panel gateway login — expected 404' },
  { method: 'GET',     path: '/api/customers/me',                  label: '(This codebase route) Customer /me — expected 404' },
];

function StatusPill({ code }) {
  if (code === null || code === undefined) {
    return <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Loader2 className="animate-spin" size={12}/> pending</span>;
  }
  const ok = code >= 200 && code < 400;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${ok ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
      {ok ? <CheckCircle2 size={12}/> : <XCircle size={12}/>}
      HTTP {code}
    </span>
  );
}

export default function Phase0Check() {
  const [rows, setRows] = useState(() => PROBES.map(p => ({ ...p, code: null, sizeKB: null, elapsedMs: null, error: null, snippet: null })));
  const [openapi, setOpenapi] = useState(null); // parsed openapi.json

  useEffect(() => {
    let alive = true;
    (async () => {
      for (let i = 0; i < PROBES.length; i++) {
        const { method, path } = PROBES[i];
        const t0 = performance.now();
        try {
          const req = method === 'OPTIONS'
            ? readAxios.options(path)
            : readAxios.get(path, { responseType: (path === '/docs' ? 'text' : 'text') });
          const res = await req;
          const elapsed = Math.round(performance.now() - t0);
          const raw = typeof res.data === 'string' ? res.data : JSON.stringify(res.data);
          const sizeKB = Math.round((raw?.length || 0) / 1024 * 10) / 10;
          if (!alive) return;
          setRows(prev => prev.map((r, idx) => idx === i ? {
            ...r, code: res.status, sizeKB, elapsedMs: elapsed,
            snippet: raw ? raw.slice(0, 180) : null,
          } : r));

          // Cache openapi for the second card
          if (path === '/openapi.json' && res.status === 200) {
            try {
              const parsed = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
              if (alive) setOpenapi(parsed);
            } catch (_e) { /* ignore */ }
          }
        } catch (e) {
          const elapsed = Math.round(performance.now() - t0);
          if (!alive) return;
          setRows(prev => prev.map((r, idx) => idx === i ? {
            ...r, code: 0, elapsedMs: elapsed, error: String(e?.message || e),
          } : r));
        }
      }
    })();
    return () => { alive = false; };
  }, []);

  const openApiSummary = openapi ? {
    title: openapi.info?.title,
    version: openapi.info?.version,
    pathCount: Object.keys(openapi.paths || {}).length,
    authPaths: Object.keys(openapi.paths || {}).filter(p => p.startsWith('/api/auth')),
    adminPaths: Object.keys(openapi.paths || {}).filter(p => p.startsWith('/api/admin')),
    employeePaths: Object.keys(openapi.paths || {}).filter(p => p.startsWith('/api/employee')),
  } : null;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="phase0-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <header className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-start gap-3">
            <ShieldCheck className="text-emerald-600 shrink-0 mt-1" size={28} />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Phase 0 — Read-only Android API Probe</h1>
              <p className="text-sm text-slate-600 mt-1">
                Target: <code className="bg-slate-100 px-1.5 py-0.5 rounded">{ANDROID_API}</code>
              </p>
              <p className="text-xs text-slate-500 mt-2">
                No writes are attempted. <code>REACT_APP_BACKEND_URL</code> is unchanged.
                No DNS / nginx / production DB / Android app is touched.
              </p>
            </div>
          </div>
        </header>

        {/* Probe results */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <h2 className="font-bold text-slate-900 mb-4">1. Endpoint probes (GET / OPTIONS only)</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b">
                  <th className="py-2 pr-3">Method</th>
                  <th className="py-2 pr-3">Path</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Latency</th>
                  <th className="py-2 pr-3">Size</th>
                  <th className="py-2 pr-3">Purpose</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.method + r.path} className="border-b last:border-b-0" data-testid={`phase0-probe-${r.path.replaceAll('/', '_')}`}>
                    <td className="py-2 pr-3"><code className="text-xs">{r.method}</code></td>
                    <td className="py-2 pr-3"><code className="text-xs text-slate-700">{r.path}</code></td>
                    <td className="py-2 pr-3"><StatusPill code={r.code} /></td>
                    <td className="py-2 pr-3 text-xs text-slate-500">{r.elapsedMs !== null ? `${r.elapsedMs}ms` : '—'}</td>
                    <td className="py-2 pr-3 text-xs text-slate-500">{r.sizeKB !== null ? `${r.sizeKB} KB` : '—'}</td>
                    <td className="py-2 pr-3 text-xs text-slate-600">{r.label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* OpenAPI summary */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6" data-testid="phase0-openapi">
          <h2 className="font-bold text-slate-900 mb-4">2. Android backend OpenAPI shape</h2>
          {!openApiSummary && (
            <div className="text-sm text-slate-500 inline-flex items-center gap-2">
              <Loader2 className="animate-spin" size={14}/> waiting for /openapi.json …
            </div>
          )}
          {openApiSummary && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <div className="text-xs text-slate-500 uppercase font-semibold">Title</div>
                <div className="text-slate-900">{openApiSummary.title}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <div className="text-xs text-slate-500 uppercase font-semibold">Version</div>
                <div className="text-slate-900">{openApiSummary.version}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <div className="text-xs text-slate-500 uppercase font-semibold">Total API paths</div>
                <div className="text-slate-900 font-bold text-xl">{openApiSummary.pathCount}</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                <div className="text-xs text-amber-800 uppercase font-semibold inline-flex items-center gap-1"><AlertTriangle size={12}/> Schema mismatch</div>
                <div className="text-amber-900 text-xs mt-1">This backend exposes {openApiSummary.authPaths.length} <code>/api/auth/*</code>, {openApiSummary.adminPaths.length} <code>/api/admin/*</code>, {openApiSummary.employeePaths.length} <code>/api/employee/*</code>. This preview&apos;s codebase uses <code>/api/panel/*</code> — incompatible.</div>
              </div>
              <div className="sm:col-span-2">
                <details className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <summary className="text-xs text-slate-600 cursor-pointer font-semibold uppercase">All /api/auth/* paths ({openApiSummary.authPaths.length})</summary>
                  <ul className="mt-2 space-y-1 text-xs text-slate-700 font-mono">
                    {openApiSummary.authPaths.map(p => <li key={p}>{p}</li>)}
                  </ul>
                </details>
              </div>
              <div className="sm:col-span-2">
                <details className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <summary className="text-xs text-slate-600 cursor-pointer font-semibold uppercase">/api/admin/* paths (first 20 of {openApiSummary.adminPaths.length})</summary>
                  <ul className="mt-2 space-y-1 text-xs text-slate-700 font-mono">
                    {openApiSummary.adminPaths.slice(0, 20).map(p => <li key={p}>{p}</li>)}
                  </ul>
                </details>
              </div>
            </div>
          )}
        </section>

        {/* Visual confirmation of local UI shell (no submit) */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6" data-testid="phase0-ui">
          <h2 className="font-bold text-slate-900 mb-4">3. UI shell confirmation (visual only — no submit)</h2>
          <p className="text-xs text-slate-500 mb-4">
            Existing login/signup surfaces render fine — they are pure UI. In this Phase 0 page the forms are NOT wired up; use the normal /account and /official routes to test them against the correct backend.
          </p>
          <div className="border rounded-lg p-4 bg-slate-50">
            <div className="text-sm text-slate-700 font-semibold mb-2">Current login surfaces this codebase supports:</div>
            <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
              <li><code>/account</code> — customer login/signup (email+password, forgot-OTP, Google sign-in)</li>
              <li><code>/official</code> → Gateway (stage 1) → admin/employee panel login (stage 2)</li>
              <li><code>/official/admin</code> — Super Admin portal (requires admin JWT)</li>
            </ul>
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mt-3 inline-flex items-start gap-1.5">
              <AlertTriangle size={14} className="mt-0.5 shrink-0"/> These forms currently POST to <code>REACT_APP_BACKEND_URL</code> (this preview&apos;s own backend), NOT to <code>{ANDROID_API}</code>. Wiring them to the Android backend requires rewriting to that API&apos;s <code>/api/auth/*</code> schema.
            </div>
          </div>
        </section>

        <footer className="text-xs text-slate-400 text-center py-4">
          Phase 0 diagnostic · read-only · Emergent preview
        </footer>
      </div>
    </div>
  );
}
